import React, { useState } from "react";
import { Send, RotateCcw, Search, Package, Wrench, ClipboardList } from "lucide-react";
import { Button } from "@/components";
import { useApp } from "@/context/AppContext";
import { useAppConfig } from "@/hooks/useAppConfig";
import { chatService } from "@/services/ChatService";
import { FEType } from "@/types/parts";
import { cn } from "@/lib/utils";
import "./ChatInput.css";

interface ChatInputProps {
  pageRoute?: string;
}

const ChatInput: React.FC<ChatInputProps> = ({ pageRoute = "home" }) => {
  const [query, setQuery] = useState("");
  const { getPageConfig } = useAppConfig();
  const {
    vehicleData,
    messages,
    isLoading,
    addMessage,
    clearMessages,
    setLoading,
    mode,
    setMode,
    feTypes,
    setFeTypes,
  } = useApp();

  // Get page-specific configuration
  const pageConfig = getPageConfig(pageRoute);
  const isModeEnabled = pageConfig?.allowMode || false;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!query.trim()) return;

    addMessage({
      type: "user",
      content: query,
      vehicleData,
    });

    setLoading(true);

    try {
      if (mode === 'fe') {
        // Feature Extraction mode
        const response = await chatService.searchFeatures(query, feTypes);

        if (response.success && response.data) {
          const responseMessage = chatService.generateChatResponse(
            [],
            [],
            query,
            undefined,
            undefined,
            'json',
            'fe',
            response.data
          );

          addMessage({
            type: "assistant",
            content: responseMessage.content,
            mode: 'fe',
            feResponse: responseMessage.feResponse,
          });
        } else {
          addMessage({
            type: "assistant",
            content:
              response.error ||
              "Sorry, I couldn't extract features. Please try again with different terms.",
          });
        }
      } else {
        // Agentic mode (existing RLHF)
        const response = await chatService.searchParts(vehicleData, query);

        if (response.success) {
          if (response.responseType === "markdown") {
            const responseMessage = chatService.generateChatResponse(
              [],
              [],
              query,
              undefined,
              response.markdownContent,
              "markdown",
              'agentic'
            );

            addMessage({
              type: "assistant",
              content: responseMessage.content,
              responseType: responseMessage.responseType,
              markdownContent: responseMessage.markdownContent,
              mode: 'agentic',
            });
          } else if (response.parts || response.vmrsParts) {
            const responseMessage = chatService.generateChatResponse(
              response.parts || [],
              response.vmrsParts || [],
              query,
              response.pt,
              undefined,
              "json",
              'agentic'
            );

            addMessage({
              type: "assistant",
              content: responseMessage.content,
              pcdbParts: responseMessage.pcdbParts,
              vmrsParts: responseMessage.vmrsParts,
              pt: response.pt,
              responseType: responseMessage.responseType,
              mode: 'agentic',
            });
          } else {
            addMessage({
              type: "assistant",
              content:
                "Sorry, I couldn't find any parts for your search. Please try again with different terms.",
            });
          }
        } else {
          addMessage({
            type: "assistant",
            content:
              response.error ||
              "Sorry, I couldn't find any parts for your search. Please try again with different terms.",
          });
        }
      }
    } catch (error) {
      console.error("Search error:", error);
      addMessage({
        type: "assistant",
        content:
          "Sorry, there was an error processing your request. Please try again.",
      });
    } finally {
      setLoading(false);
      setQuery("");
    }
  };

  const handleClearChat = () => {
    clearMessages();
  };

  const feTypeOptions = [
    { label: 'Components', value: 'components' as FEType, icon: Package },
    { label: 'Repairs', value: 'repairs' as FEType, icon: Wrench },
    { label: 'Repair Jobs', value: 'repair_jobs' as FEType, icon: ClipboardList },
  ];

  const toggleFeType = (type: FEType) => {
    if (feTypes.includes(type)) {
      // Don't allow deselecting if it's the last one
      if (feTypes.length > 1) {
        setFeTypes(feTypes.filter(t => t !== type));
      }
    } else {
      setFeTypes([...feTypes, type]);
    }
  };

  return (
    <div className="chat-input-container-modern">
      <div className="chat-input-wrapper-modern">
        <form onSubmit={handleSubmit} className="chat-input-form-modern">
          <div className="input-section-modern">
            <div className="input-icon-modern">
              <Search size={20} />
            </div>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={
                pageConfig?.searchPlaceholder ||
                "Search for parts... (e.g., brake pads, oil filter)"
              }
              className="chat-input-modern"
              disabled={isLoading}
            />
          </div>

          <div className="actions-section-modern">
            {/* Feature Type Chips - Only visible in FE mode */}
            {isModeEnabled && mode === 'fe' && (
              <div className="feature-type-chips">
                {feTypeOptions.map((opt) => {
                  const Icon = opt.icon;
                  const isActive = feTypes.includes(opt.value);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      className={cn('feature-chip', isActive && 'feature-chip-active')}
                      onClick={() => toggleFeType(opt.value)}
                      disabled={isLoading}
                    >
                      <Icon size={14} />
                      <span>{opt.label}</span>
                    </button>
                  );
                })}
              </div>
            )}

            <div className="action-buttons">
              {messages.length > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleClearChat}
                  className="clear-button-modern"
                  disabled={isLoading}
                >
                  <RotateCcw size={16} />
                  <span className="button-text">Clear</span>
                </Button>
              )}
              <Button
                type="submit"
                size="sm"
                disabled={!query.trim() || isLoading || (mode === 'fe' && feTypes.length === 0)}
                className="send-button-modern"
              >
                <Send size={16} />
                <span className="button-text">Search</span>
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChatInput;
